/**
 * Cart Recommendations — registers the <cw-cart-recommendations> custom element
 * and renders ONE mixed product carousel inside the cart drawer, blending two
 * sources:
 *
 *   1. "On sale" — products whose Shopify product_type == "SALE", ordered
 *      best-selling first, fetched from /apps/cart-upsell/recommendations.
 *   2. "Recently viewed" — products the customer browsed (tracked client-side in
 *      localStorage), hydrated via Shopify's native /products/{handle}.js.
 *
 * The two are interleaved 2:1 (on-sale : recently-viewed), on-sale first,
 * de-duplicated, and capped at 15 — see mergeRecommendations(). Too few/zero
 * recently-viewed backfills with more on-sale best-sellers.
 *
 * Mutual exclusivity: the same proxy endpoint returns { suppressed: true } when
 * the Cart Upsell is active, in which case this widget renders nothing. Only one
 * of <bw-cart-upsell> / <cw-cart-recommendations> ever shows in the drawer.
 *
 * Visual parity: the carousel markup, card structure and CSS are an exact copy of
 * the <bw-cart-upsell> widget — same `bw-` class names, same BASE_CSS — so the two
 * are pixel-identical. Because the widgets are mutually exclusive (only one ever
 * renders) the shared `bw-` classes never collide at runtime.
 *
 * Card text: recommendation products have no merchant-authored description, so the
 * product name is rendered in `.bw-product-description` — the upsell hides
 * `.bw-product-title` via custom-css and shows `.bw-product-description` instead,
 * so this keeps the visible text in the same (shown) element.
 */
(function () {
  if (customElements.get("cw-cart-recommendations")) return;

  const PROXY_BASE = "/apps/cart-upsell";
  const RECENTLY_VIEWED_KEY = "cw_recently_viewed";
  const RECENTLY_VIEWED_CAP = 20;

  /* ---------------------------------------------------------------------------
   * Recently-viewed tracker — runs on every page load via the head embed.
   * ------------------------------------------------------------------------- */

  function currentProductHandle() {
    // Prefer Shopify's analytics meta when present (most reliable).
    const metaHandle = window.ShopifyAnalytics?.meta?.product?.handle;
    if (metaHandle) return metaHandle;
    // Fall back to the URL: /products/<handle> (tolerates locale prefixes and
    // trailing /variant segments or query strings).
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function readRecentlyViewed() {
    try {
      const raw = window.localStorage.getItem(RECENTLY_VIEWED_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter((h) => typeof h === "string") : [];
    } catch {
      return [];
    }
  }

  function recordRecentlyViewed() {
    const handle = currentProductHandle();
    if (!handle) return;
    try {
      const existing = readRecentlyViewed().filter((h) => h !== handle);
      existing.unshift(handle);
      window.localStorage.setItem(
        RECENTLY_VIEWED_KEY,
        JSON.stringify(existing.slice(0, RECENTLY_VIEWED_CAP)),
      );
    } catch {
      /* localStorage unavailable (private mode, etc.) — ignore */
    }
  }

  recordRecentlyViewed();

  /* ---------------------------------------------------------------------------
   * Data fetching.
   * ------------------------------------------------------------------------- */

  async function fetchCart() {
    try {
      const r = await fetch("/cart.js", { credentials: "same-origin" });
      if (!r.ok) return null;
      return await r.json();
    } catch {
      return null;
    }
  }

  // Calls the app proxy. Returns { suppressed, onSale, rvStamps } or null on error.
  async function fetchRecommendations(cart) {
    const productIds = (cart?.items ?? [])
      .map((i) => "gid://shopify/Product/" + i.product_id)
      .join(",");
    const variantIds = (cart?.items ?? [])
      .map((i) => "gid://shopify/ProductVariant/" + i.variant_id)
      .join(",");
    const url = new URL(PROXY_BASE + "/recommendations", window.location.origin);
    if (productIds) url.searchParams.set("cart_products", productIds);
    if (variantIds) url.searchParams.set("cart_variants", variantIds);
    // Recently-viewed handles: /products/{handle}.js has no metafields, so the
    // proxy looks up their edit_stamp labels for us.
    const rvHandles = readRecentlyViewed().join(",");
    if (rvHandles) url.searchParams.set("rv_handles", rvHandles);
    try {
      const r = await fetch(url.toString(), { credentials: "same-origin" });
      if (!r.ok) return null;
      return await r.json();
    } catch {
      return null;
    }
  }

  // Hydrate a recently-viewed product from its handle via Shopify's native JSON.
  async function fetchProductByHandle(handle) {
    try {
      const r = await fetch(`/products/${handle}.js`, {
        credentials: "same-origin",
      });
      if (!r.ok) return null;
      return await r.json();
    } catch {
      return null;
    }
  }

  async function addToCart(variantId) {
    const numericVariantId = String(variantId).replace(
      "gid://shopify/ProductVariant/",
      "",
    );
    try {
      const r = await fetch("/cart/add.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{ id: parseInt(numericVariantId, 10), quantity: 1 }],
        }),
      });
      if (!r.ok) throw new Error("add failed");
      window.dispatchEvent(new CustomEvent("cart:refresh"));
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      window.dispatchEvent(new CustomEvent("variant:added"));
      return true;
    } catch (err) {
      console.error("[cw-cart-recommendations] add to cart failed:", err);
      return false;
    }
  }

  /* ---------------------------------------------------------------------------
   * Formatting helpers (ported from cart-upsell.js).
   * ------------------------------------------------------------------------- */

  function formatPrice(value) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "decimal",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      return value.toFixed(2);
    }
  }

  function escapeHTML(str) {
    return String(str).replace(
      /[&<>"']/g,
      (s) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[s],
    );
  }

  // Bare numeric product id, regardless of source (proxy returns GIDs,
  // /products/{handle}.js returns a numeric id) — used to de-duplicate across
  // the on-sale and recently-viewed sources.
  function numericProductId(id) {
    return String(id).replace("gid://shopify/Product/", "");
  }

  // Stamp colors come from merchant metaobjects and land in a style attribute —
  // accept only hex codes / plain color names, anything else is dropped.
  function safeColor(value, fallback) {
    const v = String(value ?? "").trim();
    return /^(#[0-9a-fA-F]{3,8}|[a-zA-Z]+)$/.test(v) ? v : fallback;
  }

  /* ---------------------------------------------------------------------------
   * Mixed-carousel ordering.
   *
   * Blend on-sale (P) and recently-viewed (R) into ONE list in a repeating 2:1
   * pattern (P P R | P P R | …), on-sale first, capped at `cap`:
   *   - Dedup: a product that is both on-sale and recently-viewed is kept once,
   *     as on-sale (recently-viewed copies are dropped up front).
   *   - Backfill: if a slot's preferred source is exhausted, take from the other
   *     source instead — so too-few / zero recently-viewed yields a carousel of
   *     pure on-sale best-sellers and slots are never left empty.
   * ------------------------------------------------------------------------- */
  function mergeRecommendations(onSale, recentlyViewed, cap) {
    const onSaleIds = new Set(onSale.map((c) => numericProductId(c.id)));
    const recent = recentlyViewed.filter(
      (c) => !onSaleIds.has(numericProductId(c.id)),
    );

    const PATTERN = ["P", "P", "R"];
    const result = [];
    let pi = 0;
    let ri = 0;
    let k = 0;
    while (result.length < cap && (pi < onSale.length || ri < recent.length)) {
      const wantsOnSale = PATTERN[k % PATTERN.length] === "P";
      const primary = wantsOnSale ? onSale : recent;
      const primaryIdx = wantsOnSale ? pi : ri;
      if (primaryIdx < primary.length) {
        result.push(primary[primaryIdx]);
        if (wantsOnSale) pi++;
        else ri++;
      } else if (wantsOnSale && ri < recent.length) {
        result.push(recent[ri++]);
      } else if (!wantsOnSale && pi < onSale.length) {
        result.push(onSale[pi++]);
      }
      k++;
    }
    return result;
  }

  /* ---------------------------------------------------------------------------
   * Custom element.
   * ------------------------------------------------------------------------- */

  class CartRecommendationsElement extends HTMLElement {
    constructor() {
      super();
      this._rendered = false;
      this._autoplayIds = [];
    }

    connectedCallback() {
      if (this._rendered) return;
      this._rendered = true;
      this._init();
    }

    disconnectedCallback() {
      this._stopAutoplay();
    }

    _money(value) {
      const symbol = this.getAttribute("currency-symbol") || "₪";
      return symbol + formatPrice(value);
    }

    async _init() {
      const cart = await fetchCart();
      const data = await fetchRecommendations(cart);

      // Mutual exclusivity: the upsell is active → render nothing.
      if (!data || data.suppressed) return;

      const cap =
        parseInt(this.getAttribute("max-products") || "15", 10) || 15;
      const cartProductIds = new Set(
        (cart?.items ?? []).map((i) => String(i.product_id)),
      );

      // Load both sources (each already excludes cart products). On-sale arrives
      // best-selling-first from the proxy; recently-viewed is most-recent-first.
      const recentlyViewed = await this._loadRecentlyViewed(
        cartProductIds,
        cap,
        data.rvStamps || {},
      );
      const onSale = this._normalizeOnSale(data.onSale || [], cartProductIds);

      // Blend into ONE carousel: 2 on-sale : 1 recently-viewed, on-sale first,
      // de-duplicated (a product that is both counts as on-sale), capped at `cap`,
      // backfilling from on-sale when recently-viewed runs short.
      const cards = mergeRecommendations(onSale, recentlyViewed, cap);
      if (cards.length === 0) return;

      this._sections = [
        {
          key: "mixed",
          title: this.getAttribute("carousel-title") || "אולי תרצו גם",
          cards,
        },
      ];

      this._render();
    }

    // Returns normalized cards for the recently-viewed section, excluding any
    // product already in the cart, preserving most-recent-first order.
    async _loadRecentlyViewed(cartProductIds, max, rvStamps) {
      const handles = readRecentlyViewed().slice(0, max + cartProductIds.size);
      const products = await Promise.all(
        handles.map((h) => fetchProductByHandle(h)),
      );
      const cards = [];
      for (const p of products) {
        if (!p || cartProductIds.has(String(p.id))) continue;
        const variant =
          (p.variants || []).find((v) => v.available) ||
          (p.variants || [])[0];
        if (!variant) continue;
        // /products/{handle}.js prices are in cents.
        const price = (variant.price ?? p.price ?? 0) / 100;
        const compareAt = (variant.compare_at_price ?? 0) / 100;
        cards.push({
          id: String(p.id),
          title: p.title,
          price,
          compareAtPrice: compareAt > price ? compareAt : null,
          stamps: rvStamps[p.handle] || [],
          image: p.featured_image || (p.images && p.images[0]) || null,
          imageAlt: p.title,
          variantId: variant.id,
          url: p.url || `/products/${p.handle}`,
        });
        if (cards.length >= max) break;
      }
      return cards;
    }

    // Normalize on-sale cards from the proxy (prices already in major units,
    // compareAtPrice pre-filtered to "actually marked down", variantId is a GID).
    _normalizeOnSale(onSale, cartProductIds) {
      return onSale
        .filter((p) => p && !cartProductIds.has(String(p.id).replace("gid://shopify/Product/", "")))
        .map((p) => ({
          id: String(p.id),
          title: p.title,
          price: p.price ?? 0,
          compareAtPrice: p.compareAtPrice ?? null,
          stamps: p.stamps || [],
          image: p.image || null,
          imageAlt: p.imageAlt || p.title,
          variantId: p.variantId,
          url: p.url || `/products/${p.handle}`,
        }));
    }

    _render() {
      const customCss = this.getAttribute("custom-css") || "";
      const styleTag = `<style>${BASE_CSS}\n${customCss}</style>`;

      const sectionsHTML = this._sections
        .map((section) => this._renderSection(section))
        .join("");

      // `bw-cart-upsell` reuses the upsell wrapper styles for parity; the
      // `cw-cart-recommendations` class is kept as a stable hook for this widget.
      this.innerHTML = `
        ${styleTag}
        <div class="bw-cart-upsell cw-cart-recommendations">
          ${sectionsHTML}
        </div>
      `;

      this._wireUp();
    }

    _renderSection(section) {
      const addText =
        this.getAttribute("add-to-cart-button-text") || "הוסף לסל";
      const showNav =
        (this.getAttribute("show-navigation") ?? "true") !== "false";
      return `
        <div class="bw-section" data-section="${escapeHTML(section.key)}">
          <div class="bw-section-title">${escapeHTML(section.title)}</div>
          <div class="bw-carousel" data-show-nav="${showNav}">
            ${
              showNav
                ? `<button class="bw-carousel-nav bw-carousel-prev" aria-label="Previous">›</button>`
                : ""
            }
            <div class="bw-carousel-track">
              ${section.cards
                .map(
                  (card) =>
                    `<div class="bw-carousel-card">${this._renderCard(card, addText)}</div>`,
                )
                .join("")}
            </div>
            ${
              showNav
                ? `<button class="bw-carousel-nav bw-carousel-next" aria-label="Next">‹</button>`
                : ""
            }
          </div>
        </div>
      `;
    }

    // Mirrors the upsell card (_renderCardInner): image on top, centered text
    // block (product name + price), then the add-to-cart button. Marked-down
    // products get the upsell's crossed-out original price + "-X%" badge;
    // merchant edit_stamp labels render as pills over the image.
    _renderCard(card, addText) {
      const img = card.image
        ? `<img class="bw-product-image" src="${escapeHTML(card.image)}" alt="${escapeHTML(card.imageAlt || card.title)}" />`
        : `<div class="bw-product-image bw-product-image--placeholder"></div>`;

      const hasCompare =
        card.compareAtPrice && card.compareAtPrice > card.price;
      const pct = hasCompare
        ? Math.round((1 - card.price / card.compareAtPrice) * 100)
        : 0;
      const priceRow = hasCompare
        ? `<span class="bw-price-original">${this._money(card.compareAtPrice)}</span>
           <span class="bw-price">${this._money(card.price)}</span>
           ${pct > 0 ? `<span class="bw-price-pct">-${pct}%</span>` : ""}`
        : `<span class="bw-price">${this._money(card.price)}</span>`;

      const stamps = (card.stamps || [])
        .map(
          (s) =>
            `<span class="bw-stamp" style="background:${safeColor(s.color, "#111")};color:${safeColor(s.textColor, "#fff")};">${escapeHTML(s.text)}</span>`,
        )
        .join("");

      return `
        <div class="bw-card-body">
          <div class="bw-card-text">
            <div class="bw-product-description">${escapeHTML(card.title)}</div>
            <div class="bw-price-row">
              ${priceRow}
            </div>
          </div>
          ${img}
          ${stamps ? `<div class="bw-stamps">${stamps}</div>` : ""}
        </div>
        <button
          class="bw-carousel-add-to-cart-btn"
          data-variant-id="${escapeHTML(card.variantId)}"
        >${escapeHTML(addText)}</button>
      `;
    }

    _wireUp() {
      const addText =
        this.getAttribute("add-to-cart-button-text") || "הוסף לסל";
      const soldOutText = this.getAttribute("sold-out-button-text") || "Sold out";

      this.querySelectorAll("[data-variant-id]").forEach((btn) => {
        btn.addEventListener("click", async (e) => {
          const target = e.currentTarget;
          const variantId = target.getAttribute("data-variant-id");
          if (!variantId) return;
          target.disabled = true;
          target.textContent = "…";
          const ok = await addToCart(variantId);
          if (!ok) {
            target.disabled = false;
            target.textContent = soldOutText || addText;
            return;
          }
          target.textContent = "✓";
          setTimeout(() => {
            this._rendered = false;
            this._stopAutoplay();
            this.innerHTML = "";
            this._init();
          }, 800);
        });
      });

      this.querySelectorAll(".bw-carousel").forEach((carousel) =>
        this._wireCarousel(carousel),
      );
    }

    _wireCarousel(carousel) {
      const track = carousel.querySelector(".bw-carousel-track");
      if (!track) return;
      const prev = carousel.querySelector(".bw-carousel-prev");
      const next = carousel.querySelector(".bw-carousel-next");
      const cardWidth = () => {
        const first = track.querySelector(".bw-carousel-card");
        return first ? first.getBoundingClientRect().width + 12 : 200;
      };
      if (prev)
        prev.addEventListener("click", () =>
          track.scrollBy({ left: -cardWidth(), behavior: "smooth" }),
        );
      if (next)
        next.addEventListener("click", () =>
          track.scrollBy({ left: cardWidth(), behavior: "smooth" }),
        );

      const updateCurrent = () => {
        const cards = track.querySelectorAll(".bw-carousel-card");
        if (cards.length === 0) return;
        const trackRect = track.getBoundingClientRect();
        const trackCenter = trackRect.left + trackRect.width / 2;
        let closest = cards[0];
        let closestDist = Infinity;
        cards.forEach((card) => {
          const r = card.getBoundingClientRect();
          const d = Math.abs(r.left + r.width / 2 - trackCenter);
          if (d < closestDist) {
            closestDist = d;
            closest = card;
          }
        });
        cards.forEach((card) =>
          card.classList.toggle("bw-carousel-card--current", card === closest),
        );
      };
      track.addEventListener("scroll", updateCurrent, { passive: true });
      requestAnimationFrame(updateCurrent);

      const autoplay =
        (this.getAttribute("autoplay-enabled") ?? "false") === "true";
      if (autoplay) {
        const delay = parseInt(
          this.getAttribute("autoplay-delay") || "3500",
          10,
        );
        this._startAutoplay(track, delay);
      }
    }

    // RTL-aware autoplay, matching the upsell. Each carousel track keeps its own
    // interval id so both sections can autoplay independently.
    _startAutoplay(track, delay) {
      const isRTL =
        getComputedStyle(track).direction === "rtl" ||
        document.documentElement.dir === "rtl";
      const dir = isRTL ? -1 : 1;
      const step = () => {
        const cardW =
          (track.querySelector(".bw-carousel-card")?.getBoundingClientRect()
            .width ?? 0) + 12;
        const max = track.scrollWidth - track.clientWidth;
        const atEnd = isRTL
          ? track.scrollLeft - cardW < -max
          : track.scrollLeft + cardW > max;
        if (atEnd) {
          track.scrollTo({ left: 0, behavior: "smooth" });
        } else {
          track.scrollBy({ left: dir * cardW, behavior: "smooth" });
        }
      };
      const id = setInterval(step, delay);
      this._autoplayIds.push(id);
      const stop = () => {
        clearInterval(id);
        this._autoplayIds = this._autoplayIds.filter((x) => x !== id);
      };
      track.addEventListener("mouseenter", stop);
      track.addEventListener("touchstart", stop, { passive: true });
      track.addEventListener("pointerdown", stop, { passive: true });
    }

    _stopAutoplay() {
      this._autoplayIds.forEach((id) => clearInterval(id));
      this._autoplayIds = [];
    }
  }

  // Exact copy of the <bw-cart-upsell> base styles (only the recommendations-only
  // `.bw-section` / `.bw-section-title` scaffolding is appended) so the two
  // carousels render pixel-identically.
  const BASE_CSS = `
    .bw-cart-upsell { display: block; margin: 1rem 0; }
    .bw-cart-upsell { font-size: 14px; margin-bottom: 0.75rem; }
    .bw-product-description { font-size: 14px; }
    .bw-block-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      border-radius: 0.5rem;
      padding: 0.5rem;
    }
    .bw-block-card {
      border-radius: 0.5rem;
      padding: 0.75rem;
      background: #fff;
      border: 1px solid #e5e5e5;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      flex-direction: row-reverse;
      position: relative;
    }
    .bw-block-card .bw-card-text { flex: 1; min-width: 0; }
    .bw-block-card .bw-carousel-add-to-cart-btn {
      display: inline-block;
      width: auto;
      margin-top: 0;
      flex: 0 0 auto;
      align-self: center;
      min-width: 70px;
    }
    .bw-card-body { display: flex; align-items: center; gap: 0.75rem; flex-direction: row-reverse; position: relative; }
    .bw-card-text { flex: 1; min-width: 0; }
    .bw-product-title { font-weight: 600; font-size: 14px; }
    .bw-product-description { font-size: 13px; color: #555; }
    .bw-price-row { display: flex; gap: 0.5rem; align-items: baseline; margin-top: 0.25rem; }
    .bw-price-original { text-decoration: line-through; color: #999; font-size: 13px; }
    .bw-price { font-weight: 600; font-size: 13px; }
    .bw-price-pct {
      font-size: 12px;
      color: #fff;
      background: #ff5c00;
      padding: 0 2px;
      direction: ltr;
      position: absolute;
      top: 5px;
      left: 5px;
    }
    .bw-product-image {
      width: 64px; height: 64px; object-fit: cover; border-radius: 0.5rem; flex-shrink: 0;
    }
    .bw-product-image--placeholder { background: #f3f3f3; }
    .bw-carousel-add-to-cart-btn {
      display: block;
      margin-top: 0.5rem;
      padding: 0.3rem 1rem;
      border: 0;
      background: #111;
      color: #fff;
      cursor: pointer;
      width: 30%;
      border-radius: 0.5rem !important;
      font-size: 12px;
    }
    .bw-carousel-add-to-cart-btn:disabled { opacity: 0.6; cursor: default; }
    .bw-carousel { position: relative; }
    .bw-carousel-track {
      display: flex;
      gap: 0.75rem;
      overflow-x: auto;
      scroll-snap-type: x proximity;
      scrollbar-width: none;
      padding-inline: calc(50% - 90px);
      overscroll-behavior-x: contain;
    }
    .bw-carousel-track::-webkit-scrollbar { display: none; }
    .bw-carousel-card {
      flex: 0 0 100%;
      min-width: 100px;
      padding: 0.75rem;
      border: none;
      background: #fff;
      scroll-snap-align: center;
      transform: scale(0.85);
      opacity: 0.55;
      transition: transform 0.25s ease, opacity 0.25s ease;
    }
    .bw-carousel-card.bw-carousel-card--current {
      transform: scale(1);
      opacity: 1;
    }
    .bw-carousel-card .bw-card-body {
      flex-direction: column-reverse;
      align-items: center;
      gap: 0.5rem;
    }
    .bw-carousel-card .bw-product-image {
      width: 120px;
      height: 120px;
    }
    .bw-carousel-card .bw-card-text { text-align: center; }
    .bw-carousel-card .bw-price-row {
      justify-content: center;
    }
    .bw-carousel-card .bw-carousel-add-to-cart-btn {
      display: block;
      width: 70%;
      padding: 0.3rem 1rem;
      font-size: 14px;
      margin: 12px auto 0;
    }
    .bw-carousel-nav {
      position: absolute; top: 50%; transform: translateY(-50%);
      width: 32px; height: 32px; border-radius: 50%;
      background: #fff; border: 1px solid #ddd; cursor: pointer; z-index: 1;
      font-size: 18px;
    }
    .bw-carousel-prev { left: -8px; }
    .bw-carousel-next { right: -8px; }

    /* Recommendations-only scaffolding (the upsell has a single carousel and no
       per-section headings). */
    /* Merchant edit_stamp labels — same look and top-left spot as the "-X%"
       badge (square corners, colors inlined per stamp). */
    .bw-stamps {
      position: absolute;
      top: 5px;
      left: 5px;
      display: flex;
      flex-direction: column;
      gap: 2px;
      align-items: flex-start;
      z-index: 1;
      pointer-events: none;
    }
    /* when the -X% badge is present it owns the top-left corner — start the
       stamps just below it */
    .bw-card-body:has(.bw-price-pct) .bw-stamps { top: 26px; }
    .bw-stamp {
      font-size: 12px;
      line-height: 1.5;
      padding: 0 2px;
      white-space: nowrap;
    }
    .bw-section { margin-bottom: 1.25rem; }
    .bw-section:last-child { margin-bottom: 0; }
    .bw-section-title {
      font-weight: 600;
      font-size: 15px;
      margin-bottom: 0.5rem;
      text-align: center;
    }

    /* custom-css overrides — copied verbatim from the upsell carousel block so
       the two are pixel-identical: hide the product title, show the description,
       recolor the price orange, and restyle the card + button. */
    .bw-carousel-card { width: fit-content !important; border-radius: 1rem !important; }
    .bw-carousel-add-to-cart-btn { background:#454545 !important; border-radius: 0.5rem !important; }
    .bw-product-title { display:none; }
    .bw-price { color:#ff5c00; font-size:13px !important; }
    .bw-product-description { font-size:14px; line-height: var(--text-lg--line-height); }
  `;

  customElements.define("cw-cart-recommendations", CartRecommendationsElement);
})();
